import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { IFilter } from 'types/common/query'
import { handleError } from 'API'
import { getIconDetail, createIcon, getIcon, updateIcon } from 'API/icons'
import { EIconShape, EIconType, IIconBuilder, IIconBuilderWithRelations } from './../interfaces/iconBuilder/index'
import { defaultIconIds } from './../pages/IconBuilderPage/constants'

export default class IconBuilderStore {
  rootStore: RootStore

  iconBuilders: IIconBuilderWithRelations[] = []
  documentTypeIcons: IIconBuilderWithRelations[] = []
  stepIcons: IIconBuilderWithRelations[] = []
  blockIcons: IIconBuilderWithRelations[] = []
  iconBuilderLength: number = 0
  iconDetail: IIconBuilderWithRelations = {}

  selectedIcon: IIconBuilderWithRelations = { shape: EIconShape.SQUARE }

  loading: boolean = false

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }
  async fetchCMSIconList(filter: IFilter<IIconBuilder> = {}): Promise<void> {
    if (this.loading) return
    this.loading = true
    try {
      const iconBuilderList = await getIcon(filter)
      const defaultIcons: IIconBuilderWithRelations[] = iconBuilderList.filter(icon =>
        defaultIconIds.includes(icon?.id ?? 0)
      )
      const sortedIcons: IIconBuilderWithRelations[] = [
        ...defaultIcons,
        ...iconBuilderList.filter(icon => defaultIconIds.includes(icon?.id ?? 0) === false)
      ]
      this.iconBuilders = sortedIcons
      this.documentTypeIcons = sortedIcons.filter(icon => icon.type === EIconType.DOCUMENT_TYPE)
      this.stepIcons = sortedIcons.filter(icon => icon.type === EIconType.STEP)
      this.blockIcons = sortedIcons.filter(icon => icon.type === EIconType.BLOCK)
    } catch (error: any) {
      handleError(error as Error, 'stores/iconBuilderStore.ts', 'fetchCMSIconList')
    }
    this.loading = false
  }

  getIconById(id: number): IIconBuilderWithRelations | undefined {
    return this.iconBuilders.find(icon => icon.id === id)
  }

  async fetchDocumentTypeIconList(
    filter: IFilter<IIconBuilder> = {
      where: {
        type: EIconType.DOCUMENT_TYPE
      }
    }
  ): Promise<void> {
    try {
      const iconBuilderList = await getIcon(filter)
      this.documentTypeIcons = iconBuilderList
    } catch (error: any) {
      handleError(error as Error, 'stores/iconBuilderStore.ts', 'fetchDocumentTypeIconList')
    }
  }

  async fetchBlockIconList(
    filter: IFilter<IIconBuilder> = {
      where: {
        type: EIconType.BLOCK
      }
    }
  ): Promise<void> {
    try {
      const iconBuilderList = await getIcon(filter)
      this.blockIcons = iconBuilderList
    } catch (error: any) {
      handleError(error as Error, 'stores/iconBuilderStore.ts', 'fetchBlockIconList')
    }
  }

  async fetchStepIconList(
    filter: IFilter<IIconBuilder> = {
      where: {
        type: EIconType.STEP
      }
    }
  ): Promise<void> {
    try {
      const iconBuilderList = await getIcon(filter)
      this.stepIcons = iconBuilderList
    } catch (error: any) {
      handleError(error as Error, 'stores/iconBuilderStore.ts', 'fetchStepIconList')
    }
  }

  async createNewIcon(iconBuilder: IIconBuilder): Promise<void> {
    try {
      const newIIconBuilder: IIconBuilder = await createIcon(iconBuilder)
      this.iconDetail = newIIconBuilder as IIconBuilder
    } catch (error: any) {
      handleError(error as Error, 'stores/iconBuilderStore.ts', 'createIIconBuilder')
    }
  }

  async fetchIconDetail(id: string, filter: IFilter<IIconBuilder> = {}): Promise<void> {
    try {
      const iconBuilders: IIconBuilderWithRelations = await getIconDetail(id, filter)
      this.iconDetail = iconBuilders
    } catch (error: any) {
      handleError(error as Error, 'stores/iconBuilderStore.ts', 'fetchIconDetail')
    }
  }

  setIconDetail(iconBuilder: IIconBuilder): void {
    this.iconDetail = iconBuilder
    this.selectedIcon = iconBuilder
  }

  resetIconDetail(): void {
    this.iconDetail = {}
  }

  async updateIcon(id: number, iconBuilder: IIconBuilder): Promise<void> {
    try {
      await updateIcon(id, iconBuilder)
    } catch (error: any) {
      handleError(error as Error, 'stores/iconBuilderStore.ts', 'updateIcon')
    }
  }

  setSelectedIcon(icon: IIconBuilderWithRelations): void {
    this.selectedIcon = icon
  }
}
